import { Icon } from "@blueprintjs/core";
import classNames from "classnames";
import { Button } from "components/buttons";
import { DataDisplayRecord } from "components/data-display";
import { DateToLocal } from "components/dates";
import { GridLayout } from "components/grid";
import { ShowHide } from "components/layout";
import { Tooltip } from "components/tooltip";
import { HelperFunctions } from "helpers";
import { LicenceCheckingLicenceConflictModal } from "pages/licence-checking/shared/licence-conflict";
import { LicenceCheckingNewConsentModal } from "pages/licence-checking/shared/new-consent";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const ON_SITE_ELIGIBLE_STATUSES = new Set([
    "AWAITINGCONSENT",
    "REQUIRESEMAIL",
    "NOCONSENT",
    "EXPIREDCONSENT",
    "REFUSEDCONSENT",
    "NODVLARECORD",
]);

function shouldShowOnSiteAction(consentStatus, invalidLicence) {
    if (consentStatus === "CONFLICTINGDETAILS") {
        return invalidLicence === true;
    }
    return ON_SITE_ELIGIBLE_STATUSES.has(consentStatus);
}

export function EmployeeLicenceCheckDetails({
    latestConsent,
    latestCheck,
    checkDetails,
    historicCheckDetails,
    validConsent = false,
    isLatestCheck = false,
    gridSize = 2,
    loading = false,
    employeeName = "",
    employeeId = "",
    onReloadData = null
}) {

    const [showConflictModal, setShowConflictModal] = useState(false);
    const [showConsentModal, setShowConsentModal] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    function onConflictModalOpen() {
        setShowConflictModal(true);
    }

    function onConflictModalClose() {
        setShowConflictModal(false);
    }

    function onModalConfirm() {
        if (onReloadData != null) {
            onReloadData();
        }
    }

    function onConsentModalOpen() {
        setShowConsentModal(true);
    }

    function onConsentModalClose() {
        setShowConsentModal(false);
    }

    function renderConsentActionButton(consentDetails) {

        let icon = "repeat";
        let intent = "primary"
        let buttonText = "Restart Declaration";
        let buttonAction = onConsentModalOpen;

        switch (consentDetails?.statusKey) {
            case "CONFLICTINGDETAILS":
                if (consentDetails?.invalidLicence !== true) {
                    icon = "error";
                    intent = "danger";
                    buttonText = "Compare Conflicts";
                    buttonAction = onConflictModalOpen;
                }
                break;
            case "AWAITINGCONSENT":
                icon = "envelope"
                buttonText = "Resend Declaration Email"
                break;
            case "REQUIRESEMAIL":
                icon = "plus";
                buttonText = "Add Email Address"
                break;
            case "NOCONSENT":
                icon = "plus";
                buttonText = "Enroll in Licence Checking";
                break;
        }

        return <Button text={buttonText} icon={icon} intent={intent} onClick={buttonAction} />;
    }

    function renderOnSiteActionButton(consentDetails) {
        if (!shouldShowOnSiteAction(consentDetails?.statusKey, consentDetails?.invalidLicence)) {
            return null;
        }
        return (
            <Button
                text="Perform declaration on site"
                icon="person"
                intent="primary"
                onClick={() => {
                    navigate(`/licence-checking/declaration/on-site/${employeeId}`, {
                        state: { returnTo: location.pathname, cancelTo: location.pathname },
                    });
                }}
            />
        );
    }

    return (
        <>
            <ShowHide
                evaluator={latestConsent?.statusKey !== "VALIDCONSENT" && isLatestCheck}
                show={(
                    <>
                        <ShowHide
                            evaluator={validConsent}
                            show={(
                                <div className="spacer-bottom">
                                    <h3>Pending Declaration Details</h3>
                                </div>
                            )}
                        />

                        <div className="spacer-bottom">

                            <GridLayout numberOfColumns={gridSize} gap={40}>
                                <DataDisplayRecord
                                    header={<h4>Declaration Status</h4>}
                                    data={latestConsent?.invalidLicence === true ? "Declaration details do not match with the licence issuing authority" : latestConsent?.status}
                                    loading={loading}
                                />
                                <ShowHide
                                    evaluator={latestConsent?.statusKey !== "NOCONSENT"}
                                    show={(
                                        <>
                                            <DataDisplayRecord
                                                header={<h4>Employee Email</h4>}
                                                data={latestConsent?.email}
                                                loading={loading}
                                            />
                                            <DataDisplayRecord
                                                header={<h4>Date Last Declaration Email Sent</h4>}
                                                data={(
                                                    <DateToLocal format="DD/MM/YYYY">{latestConsent?.emailLastSent ?? "-"}</DateToLocal>
                                                )}
                                                loading={loading}
                                            />
                                            <ShowHide
                                                evaluator={latestConsent?.completedByManagerEmail != null && latestConsent.completedByManagerEmail !== ""}
                                                show={(
                                                    <DataDisplayRecord
                                                        header={<h4>Completed On-Site By</h4>}
                                                        data={latestConsent?.completedByManagerEmail}
                                                        loading={loading}
                                                    />
                                                )}
                                            />
                                        </>
                                    )}
                                />

                            </GridLayout>
                        </div>
                        <div className="button-row spacer-bottom">
                            {renderConsentActionButton(latestConsent)}
                            {renderOnSiteActionButton(latestConsent)}
                        </div>

                    </>
                )}
            />

            <ShowHide
                evaluator={historicCheckDetails != null && !isLatestCheck}
                show={(
                    <>
                        <div className="spacer-bottom">
                            <h3>Check Details</h3>
                        </div>
                        <div className="spacer-bottom">
                            <GridLayout numberOfColumns={gridSize} gap={40}>
                                <DataDisplayRecord
                                    header={<h4>Check Date</h4>}
                                    data={<DateToLocal format="DD/MM/YYYY">{historicCheckDetails?.date ?? "-"}</DateToLocal>}
                                    loading={loading}
                                />
                                <DataDisplayRecord
                                    header={<h4>Check Type</h4>}
                                    data={historicCheckDetails?.type}
                                    loading={loading}
                                />
                                <DataDisplayRecord
                                    header={<h4>Check Detail</h4>}
                                    data={historicCheckDetails?.typeDetail}
                                    loading={loading}
                                />
                                <DataDisplayRecord
                                    header={<h4>Check Completed By</h4>}
                                    data={historicCheckDetails?.completedBy}
                                    loading={loading}
                                />
                            </GridLayout>
                        </div>
                    </>
                )}
            />

            <ShowHide
                evaluator={latestCheck != null}
                show={(
                    <>
                        <div className="spacer-bottom">
                            <h3>{`${isLatestCheck ? "Current " : ""}Licence Check Results`}</h3>
                        </div>
                        <div className="spacer-bottom">

                            <ShowHide
                                evaluator={latestCheck?.licence?.status != null}
                                show={(
                                    <GridLayout numberOfColumns={gridSize} gap={40}>
                                        <DataDisplayRecord
                                            header={<h4>Driving Licence Number</h4>}
                                            data={latestCheck?.licence?.licenceNumber}
                                            loading={loading}
                                        />
                                        <DataDisplayRecord
                                            header={<h4>Licence Status</h4>}
                                            data={(
                                                <span className={classNames({ "text-intent-danger": latestCheck?.licence?.status !== "Valid" })}>{latestCheck?.licence?.status}</span>
                                            )}
                                            loading={loading}
                                        />
                                        <ShowHide
                                            evaluator={latestCheck?.licence?.disqualifiedUntil != null}
                                            show={(
                                                <DataDisplayRecord
                                                    header={<h4>Disqualification Expiry Date</h4>}
                                                    data={(
                                                        <DateToLocal format="DD/MM/YYYY">{latestCheck?.licence?.disqualifiedUntil ?? "-"}</DateToLocal>
                                                    )}
                                                    loading={loading}
                                                />
                                            )}
                                        />

                                        <DataDisplayRecord
                                            header={<h4>Photocard Expiry Date</h4>}
                                            data={(
                                                <DateToLocal format="DD/MM/YYYY">{latestCheck?.licence?.photocardExpiry ?? "-"}</DateToLocal>
                                            )}
                                            loading={loading}
                                        />
                                        <DataDisplayRecord
                                            header={<h4>Short Term Licence</h4>}
                                            data={latestCheck?.licence?.shortTermLicence === true ? "Yes" : "No"}
                                            loading={loading}
                                        />
                                        <DataDisplayRecord
                                            header={<h4>Points Since Last Check</h4>}
                                            data={latestCheck?.licence?.pointsSinceLastCheck != null && parseInt(latestCheck?.licence?.pointsSinceLastCheck) > 0 ? <span className="text-intent-danger">{latestCheck?.licence?.pointsSinceLastCheck}</span> : <span>{latestCheck?.licence?.pointsSinceLastCheck ?? "N/A"}</span>}
                                            loading={loading}
                                        />
                                        <DataDisplayRecord
                                            header={<h4>Admin Changes Since Last Check</h4>}
                                            data={(
                                                latestCheck?.licence?.changesSinceLastCheck ?
                                                    <div className="inline-items">
                                                        <div className="inline-items">Yes</div>
                                                        <div className="inline-items">
                                                            <Tooltip content={`Since the latest check (${HelperFunctions.formatDate(latestCheck?.checkDate, "DD/MM/YYYY")}) and the previous check, administrative details on the licence have chenged. Please speak to the Employee for details.`}>
                                                                <Icon icon="info-sign" size={12} />
                                                            </Tooltip>
                                                        </div>
                                                    </div>
                                                    :
                                                    <span>No</span>

                                            )}
                                            loading={loading}
                                        />
                                        <DataDisplayRecord
                                            header={<h4>Driving Licence Origin</h4>}
                                            data={latestCheck?.licence?.licenceOrigin}
                                            loading={loading}
                                        />
                                    </GridLayout>
                                )}
                                hide={(
                                    <p>The check you are trying to view is no longer available, either because the employee left your organisation or requested to have their data deleted.</p>
                                )}
                            />
                        </div>
                    </>
                )}
            />


            <ShowHide
                evaluator={validConsent && checkDetails != null}
                show={(
                    <>
                        <div className="spacer-bottom">
                            <h3>Checking Details</h3>
                        </div>
                        <div className="spacer-bottom">

                            <GridLayout numberOfColumns={gridSize} gap={40}>
                                <DataDisplayRecord
                                    header={<h4>Last Check Date</h4>}
                                    data={<DateToLocal format="DD/MM/YYYY">{checkDetails?.lastCheck ?? "-"}</DateToLocal>}
                                    loading={loading}
                                />
                                <DataDisplayRecord
                                    header={<h4>Next Check Date</h4>}
                                    data={<DateToLocal format="DD/MM/YYYY">{checkDetails?.nextCheck ?? "-"}</DateToLocal>}
                                    loading={loading}
                                />
                                <DataDisplayRecord
                                    header={(
                                        <div className="inline-items">
                                            <div className="inline-items">
                                                <h4 className="data-display-table-field-header">Current Policy</h4>
                                            </div>
                                            <div className="inline-items">
                                                <Tooltip content={"The current policy is the policy the driver falls into with the most frequent checks depending on the driver's number of endorsements, various expiry dates, age etc."}>
                                                    <Icon icon="info-sign" size={12} />
                                                </Tooltip>
                                            </div>
                                        </div>
                                    )}
                                    data={checkDetails?.policy ?? <em>None</em>}
                                    loading={loading}
                                />
                                <DataDisplayRecord
                                    header={<h4>Declaration Expiry</h4>}
                                    data={<DateToLocal format="DD/MM/YYYY">{checkDetails?.consentExpiry ?? "-"}</DateToLocal>}
                                    loading={loading}
                                />
                            </GridLayout>
                        </div>
                    </>
                )}
            />

            <LicenceCheckingNewConsentModal
                isOpen={showConsentModal}
                employeeId={employeeId}
                emailAddress={checkDetails?.email ?? latestConsent?.email }
                employeeName={employeeName}
                onSuccess={onModalConfirm}
                onClose={onConsentModalClose}
                consentExists={latestConsent?.id != null && latestConsent?.email != null}
            />
            <LicenceCheckingLicenceConflictModal
                isOpen={showConflictModal}
                existingLicenceNumber={checkDetails?.licenceNumber}
                consentLicenceNumber={latestConsent?.licenceNumber}
                employeeName={employeeName}
                onClose={onConflictModalClose}
                onSuccess={onModalConfirm}
                employeeId={employeeId}
                emailAddress={latestConsent?.email}
                consentId={latestConsent?.id}
                licenceNumberOverridable={checkDetails?.canCopyLicenceNumber}
            />
        </>
    );
}

EmployeeLicenceCheckDetails.propTypes = {
    latestConsent: PropTypes.object,
    latestCheck: PropTypes.object,
    checkDetails: PropTypes.object,
    historicCheckDetails: PropTypes.object,
    isLatestCheck: PropTypes.bool,
    validConsent: PropTypes.bool,
    gridSize: PropTypes.number,
    loading: PropTypes.bool,
    employeeName: PropTypes.string,
    employeeId: PropTypes.string,
    onReloadData: PropTypes.func,
};
